import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { IntakeNavigation } from "./IntakeNavigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { IntakeData } from "@/lib/types";
import { SpecialtySelect } from "@/components/form/SpecialtySelect";
import {
  collectServerMediaIdsForProcedureReset,
  DETAILED_PROCEDURE_KEYS,
  deleteMediaIdsWithLogging,
} from "@/lib/patientIntakeApi";
 import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FileText,
  Calendar as CalendarIcon,
} from "lucide-react";
import { scrollToFirstError } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

function coerceDentalProcedureTypes(v: unknown): string[] {
  if (Array.isArray(v)) return (v as string[]).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

const CONDITION_OPTIONS = [
  { value: "none", label: msg`None`, rawLabel: "None" },
  { value: "diabetes", label: msg`Diabetes`, rawLabel: "Diabetes" },
  { value: "hypertension", label: msg`Hypertension`, rawLabel: "Hypertension" },
  { value: "heart_disease", label: msg`Heart Disease`, rawLabel: "Heart Disease" },
  { value: "asthma", label: msg`Asthma`, rawLabel: "Asthma" },
  { value: "arthritis", label: msg`Arthritis`, rawLabel: "Arthritis" },
  { value: "depression", label: msg`Depression/Anxiety`, rawLabel: "Depression/Anxiety" },
  { value: "thyroid", label: msg`Thyroid Disorder`, rawLabel: "Thyroid Disorder" },
  { value: "other", label: msg`Other`, rawLabel: "Other" },
] as const;

 
const SMOKING_OPTIONS = [
  { value: "no", label: msg`I do not smoke` },
  { value: "yes_1_5", label: msg`Yes, 1-5 cigarettes/day` },
  { value: "yes_5_10", label: msg`Yes, 5-10 cigarettes/day` },
  { value: "yes_over_10", label: msg`Yes, over 10/day` },
] as const;

const ALCOHOL_OPTIONS = [
  { value: "no", label: msg`No` },
  { value: "occasionally", label: msg`Occasionally` },
  { value: "regularly", label: msg`Regularly` },
  { value: "daily", label: msg`Daily` },
] as const;

const EXERCISE_OPTIONS = [
  { value: "none", label: msg`None` },
  { value: "1_2_week", label: msg`1-2 times/week` },
  { value: "3_5_week", label: msg`3-5 times/week` },
  { value: "daily", label: msg`Daily` },
] as const;


const formatPreExistingConditionSummary = (
  conditions: unknown,
  otherConditionNote: string,
  conditionLabels: Record<string, string>,
): string => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return "";
  }

  if (conditions.includes("none")) {
    return "None";
  }

  const uniqueConditions = Array.from(
    new Set(
      (conditions as string[]).filter(
        (condition) => condition && condition !== "none",
      ),
    ),
  );

  const formatted = uniqueConditions
    .map((condition) => {
      if (condition === "other") {
        return otherConditionNote
          ? `Other: ${otherConditionNote}`
          : conditionLabels[condition] ?? "Other";
      }
      return conditionLabels[condition] ?? condition.replace(/_/g, " ");
    })
    .filter(Boolean);

  if (otherConditionNote && !uniqueConditions.includes("other")) {
    formatted.push(`Other: ${otherConditionNote}`);
  }

  return formatted.join(", ");
};

const parsePreExistingConditions = (
  preExisting?: string,
): string[] => {
  if (!preExisting) {
    return [];
  }

  const normalized = preExisting.toLowerCase();

  if (normalized.includes("none")) {
    return ["none"];
  }

  const selections = CONDITION_OPTIONS.filter((option) => {
    if (option.value === "none") {
      return false;
    }
    if (option.value === "other") {
      return normalized.includes("other");
    }
    return normalized.includes(option.rawLabel.toLowerCase());
  }).map((option) => option.value);

  return Array.from(new Set(selections));
};

const extractOtherConditionNote = (preExisting?: string): string => {
  if (!preExisting) {
    return "";
  }

  const match = preExisting.match(/other[:-]?\s*(.*)$/i);
  return match ? match[1].trim() : "";
};

const mergeAllergyMedicationNotes = (
  allergiesMedications?: string,
  allergies?: string,
  medications?: string,
) => {
  if (allergiesMedications && allergiesMedications.trim().length > 0) {
    return allergiesMedications;
  }
  if (allergies && medications && allergies.trim() && medications.trim()) {
    if (allergies.trim() === medications.trim()) {
      return allergies.trim();
    }
    return `Allergies: ${allergies.trim()}\nMedications: ${medications.trim()}`;
  }
  if (allergies && allergies.trim().length > 0) {
    return allergies.trim();
  }
  if (medications && medications.trim().length > 0) {
    return medications.trim();
  }
  return "";
};

interface MedicalProcedureStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const isIOSDevice =
  typeof navigator !== "undefined" &&
  (/(iPad|iPhone|iPod)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1));

export default function MedicalProcedureStep({
  data,
  onUpdate,
  onNext,
  onBack,
}: MedicalProcedureStepProps) {
  const { i18n } = useLingui();

  const [isDentistry, setIsDentistry] = useState(() => {
    // Check both string value and potential object property
    const proc = data.procedures;
    return proc === "dentistry" || (typeof proc === 'object' && proc?.slug === "dentistry");
  });


  const conditionLabels = useMemo(
    () =>
      CONDITION_OPTIONS.reduce<Record<string, string>>((acc, option) => {
        acc[option.value] = i18n._(option.label);
        return acc;
      }, {}),
    [i18n],
  );
  const translatedConditionOptions = useMemo(
    () =>
      CONDITION_OPTIONS.map((option) => ({
        ...option,
        translatedLabel: i18n._(option.label),
      })),
    [i18n],
  );

  const [formData, setFormData] = useState(() => {
    const existingConditions =
      Array.isArray(data.currentMedicalConditions) &&
        data.currentMedicalConditions.length > 0
        ? data.currentMedicalConditions
        : parsePreExistingConditions(data.preExistingConditions);

    const mergedAllergyMedicationNotes = mergeAllergyMedicationNotes(
      data.allergiesMedications,
      data.allergies,
      data.currentMedications,
    );
 
    // Parse height if it exists (format: "3ft 5in" or similar)
    const parseHeight = (heightStr?: string) => {
      if (!heightStr) return { feet: "", inches: "" };
      const feetMatch = heightStr.match(/(\d+)\s*ft/i);
      const inchesMatch = heightStr.match(/(\d+)\s*in/i);
      return {
        feet: feetMatch ? feetMatch[1] : "",
        inches: inchesMatch ? inchesMatch[1] : "",
      };
    };
    const parsedHeight = parseHeight(data.height);

    return {
      procedures: typeof data.procedures === 'string' ? data.procedures : '',
      dateOfBirth: data.dateOfBirth || "",
      height: data.height || "", 
      heightFeet: parsedHeight.feet,
      heightInches: parsedHeight.inches,
      weight: data.weight || "",
      currentMedicalConditions: existingConditions,
      otherCondition:
        data.otherCondition || extractOtherConditionNote(data.preExistingConditions),
      pastSurgeries: data.pastSurgeries || data.majorSurgeries || "",
      allergiesMedications: mergedAllergyMedicationNotes,
      smokingHabits: data.smokingHabits || "",
      alcoholUse: data.alcoholUse || data.alcoholConsumption || "",
      exerciseFrequency: data.exerciseFrequency || "",
      dentalProcedureType: coerceDentalProcedureTypes(data.dentalProcedureType),
      dentalDiscomfort: data.dentalDiscomfort || "",
      mobilityIssues: data.mobilityIssues, 
      dietaryRestrictions: data.dietaryRestrictions,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Calculate height in cm
  const heightInCm = useMemo(() => {
    const feet = parseFloat(formData.heightFeet) || 0;
    const inches = parseFloat(formData.heightInches) || 0;
    if (feet === 0 && inches === 0) return null;
    const totalInches = feet * 12 + inches;
    return (totalInches * 2.54).toFixed(1);
  }, [formData.heightFeet, formData.heightInches]);

  // Calculate weight in kg (from pounds input)
  const weightInKg = useMemo(() => {
    const lbs = parseFloat(formData.weight);
    if (!lbs || isNaN(lbs)) return null;
    return (lbs / 2.20462).toFixed(1);
  }, [formData.weight]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "procedures"  ) {
        if(typeof value === "string" && value === "dentistry"){
          setIsDentistry(true); 
        }else{
          setIsDentistry(false); 
        } 
      }

      if (field === "heightFeet" || field === "heightInches") {
        const feet = field === "heightFeet" ? value as string : prev.heightFeet || "";
        const inches = field === "heightInches" ? value as string : prev.heightInches || "";
        if (feet || inches) {
          updated.height = `${feet}ft ${inches}in`.trim();
        } else {
          updated.height = "";
        }
      }

      return updated;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    // Clear height error if either feet or inches changes
    if ((field === "heightFeet" || field === "heightInches") && errors.height) {
      setErrors((prev) => ({ ...prev, height: "" }));
    }
  };

  const handleConditionChange = (condition: string, checked: boolean) => {
    const currentConditions = Array.isArray(formData.currentMedicalConditions)
      ? formData.currentMedicalConditions
      : [];

    if (checked) {
      let newConditions = [...currentConditions, condition];

      // If "none" is selected, remove all other conditions
      if (condition === "none") {
        newConditions = ["none"];
      } else {
        // If any other condition is selected, remove "none"
        newConditions = newConditions.filter((c) => c !== "none");
      }

      handleInputChange("currentMedicalConditions", newConditions);
    } else {
      handleInputChange(
        "currentMedicalConditions",
        currentConditions.filter((c) => c !== condition)
      );
      if (condition === "other") {
        handleInputChange("otherCondition", "");
      }
    }
  };

  const handleDentalProcedureToggle = (value: string, checked: boolean) => {
    const current = coerceDentalProcedureTypes(formData.dentalProcedureType);
    if (checked) {
      if (!current.includes(value)) {
        handleInputChange("dentalProcedureType", [...current, value]);
      }
    } else {
      handleInputChange(
        "dentalProcedureType",
        current.filter((v) => v !== value),
      );
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    
    const proceduresValue = formData.procedures;

    if (
      !proceduresValue ||
      (typeof proceduresValue === "string" && proceduresValue.trim() === "")
    ) {
      newErrors.procedures = i18n._(`Please select a procedure`);
    }
    
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = i18n._(`Date of birth is required`)  ;
    }
    if (!formData.heightFeet || !formData.heightInches) {
      newErrors.height = i18n._(`Height is required`) ;
    }
    if (!formData.weight) {
      newErrors.weight = i18n._(`Weight is required`) ;
    } 

    if (
      typeof proceduresValue === "string" &&
      proceduresValue.trim() === "dentistry" &&
      (!Array.isArray(formData.dentalProcedureType) ||
        formData.dentalProcedureType.length === 0)
    ) {
      newErrors.dentalProcedureType = i18n._(`Please select dental procedure.`) ;
    }

    // Lifestyle Validations
    if (!formData.smokingHabits) {
      newErrors.smokingHabits = i18n._(`Please select smoking habits`);
    }
    if (!formData.alcoholUse) {
      newErrors.alcoholUse = i18n._(`Please select alcohol consumption`);
    }
    if (!formData.exerciseFrequency) {
      newErrors.exerciseFrequency = i18n._(`Please select exercise frequency`);
    }

    setErrors(newErrors);

    // Find first error field and scroll to it
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
    }

    return Object.keys(newErrors).length === 0;
  };


  const [isWaitingForUpdate, setIsWaitingForUpdate] = useState(false);

  // Sync effect: Wait for data prop to update before navigating
  useEffect(() => {
    if (isWaitingForUpdate) {
       // Check if the prop now matches the local state
       // Handle both string and object cases for robustness
       const propProcedure = typeof data.procedures === 'string' ? data.procedures : (data.procedures as any)?.slug;
       const localProcedure = typeof formData.procedures === 'string' ? formData.procedures : (formData.procedures as any)?.slug;
       
       if (propProcedure === localProcedure) {
          setIsWaitingForUpdate(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
          onNext();
       }
    }
  }, [data.procedures, formData.procedures, isWaitingForUpdate, onNext]);


  const handleContinue = async () => {
    if (isSubmitting) return;
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const allergiesMedicationsNote = formData.allergiesMedications?.trim() ?? "";
      const formattedPreExisting = formatPreExistingConditionSummary(
        formData.currentMedicalConditions,
        formData.otherCondition,
        conditionLabels,
      );

      // Check if procedure has changed compared to the prop (previous saved state)
      const oldProc = typeof data.procedures === 'string' ? data.procedures : (data.procedures as any)?.slug;
      const newProc = typeof formData.procedures === 'string' ? formData.procedures : (formData.procedures as any)?.slug;
      
      const procedureChanged = oldProc !== newProc;
      const clearingUpdate: Partial<IntakeData> = {};
      
      // If procedure changed, clear procedure-specific details and previously uploaded photos
      // to prevents cross-contamination between different flows (e.g. Dental vs Optical)
      if (procedureChanged) {
          DETAILED_PROCEDURE_KEYS.forEach(key => {
              (clearingUpdate as any)[key] = "";
          });
          
          // Only clear dental specific fields if we're switching AWAY from dentistry
          // Don't clear them if we're switching TO dentistry (newProc === "dentistry")
          if (oldProc === "dentistry" && newProc !== "dentistry") {
              clearingUpdate.dentalProcedureType = [];
              clearingUpdate.dentalDiscomfort = "";
          }
          
          // Clear photos to prevent stale media from showing up in the new flow
          clearingUpdate.photos = {};
          clearingUpdate.additionalPhotos = [];
          clearingUpdate.photoMediaMap = {};
          
          // Clear uploads object completely (empty object, not object with empty arrays)
          clearingUpdate.uploads = {};
          clearingUpdate.documentReferenceLinks = [];

          const mediaIds = collectServerMediaIdsForProcedureReset(data);
          if (mediaIds.length > 0) {
            await deleteMediaIdsWithLogging(mediaIds, {
              flow: "v1-medical-procedure-step",
              reason: "specialty-change",
              formUid: data.formUid,
              oldProc,
              newProc,
            });
          }
      }

      onUpdate({
        ...formData,
        ...clearingUpdate,
        preExistingConditions: formattedPreExisting,
        currentMedications: allergiesMedicationsNote,
        allergies: allergiesMedicationsNote,
        majorSurgeries: formData.pastSurgeries?.trim() ?? "",
        alcoholConsumption: formData.alcoholUse,
      });

      // If procedure changed, wait for prop update to ensure index.tsx re-renders correct steps
      if (procedureChanged) {
          setIsWaitingForUpdate(true);
      } else {
          // No change, safe to proceed immediately
          window.scrollTo({ top: 0, behavior: "smooth" });
          onNext();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  const years = Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => 1900 + i).reverse();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handleYearChange = (year: string) => {
    const newDate = new Date(calendarMonth);
    newDate.setFullYear(parseInt(year));
    setCalendarMonth(newDate);
  };

  const handleMonthChange = (month: string) => {
    const newDate = new Date(calendarMonth);
    newDate.setMonth(months.indexOf(month));
    setCalendarMonth(newDate);
  };

  useEffect(() => {
    if (formData.dateOfBirth) {
      setCalendarMonth(new Date(formData.dateOfBirth));
    }
  }, [formData.dateOfBirth]);

  useEffect(() => {
    if (!isCalendarOpen) {
      setIsMonthDropdownOpen(false);
      setIsYearDropdownOpen(false);
    }
  }, [isCalendarOpen]);

  return (
    <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6 px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div className="text-center space-y-2 sm:space-y-3 py-2 sm:py-4">


        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
        </div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
          <Trans>Medical Information &amp; Travel Details</Trans>
        </h1>
        <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
          <Trans>
            Help us understand your medical needs, travel plans, and health
            background for personalized care.
          </Trans>

        </p>


      </div>

      {/* Main Form */}
      <div className="bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-8">
        <div className="space-y-3 md:space-y-6 lg:space-y-8">
          {/* 1. Procedure Selection */}
          <div className="bg-white md:rounded-xl pb-6 md:pb-6 lg:pb-6 md:p-6 border-b border-gray-100 md:border md:border-gray-200 last:border-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#166B93] rounded-full flex items-center justify-center text-white font-bold">
                1
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Procedure of Interest</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Select the medical procedure you're interested in</Trans>
                </p>
                <p className="text-xs sm:text-sm text-[#166B93] mt-1 font-medium">
                  <Trans>Pick one at a time, you can come back and fill out others later</Trans>
                </p>
              </div>
            </div>

            <div className="mb-3 sm:mb-4">
              <Label className="text-sm sm:text-base font-medium text-gray-700 mb-4 block">
                <Trans>What procedure are you interested in? *</Trans>
              </Label>
            </div>

            <SpecialtySelect
              value={typeof formData.procedures === 'string' ? formData.procedures : ''}
              email={data.email}
              onChange={(slug: string) => {
                handleInputChange("procedures", slug);
              }}
              className="mb-2 sm:mb-3"
              aria-invalid={!!errors.procedures}
              aria-describedby={errors.procedures ? "procedures-error" : undefined}>
            </SpecialtySelect>

            
            {errors.procedures && (
              <p id="procedures-error" className="text-red-500 text-sm mt-2">{errors.procedures}</p>
            )}
          </div>



          {/* 2. Health Background */}
          <div className="bg-white md:rounded-xl pb-6 md:pb-6 md:p-6 border-b border-gray-100 md:border md:border-gray-200 last:border-0 pt-4 md:pt-6">

            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#FF9142] rounded-full flex items-center justify-center text-white font-bold">
                2
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Health Background</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Your medical history helps us provide better care</Trans>
                </p>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-6">
              {/* Basic Health Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">

                <div className="w-full sm:col-span-2 lg:col-span-1">
                  <Label
                    htmlFor="height"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block">
                    <Trans>Height *</Trans>
                  </Label>
 
                  {/* XXXXX */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 w-full">
                    <div className="w-full sm:flex-1">
                      {isIOSDevice ? (
                        <select
                          value={formData.heightFeet}
                          onChange={(e) =>
                            handleInputChange("heightFeet", e.target.value)
                          }
                          className={`h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10 ${
                            errors.height ? "border-red-500" : "border-gray-200"
                          }`}
                        >
                          <option value="">{i18n._(`Feet`)}</option>
                          {Array.from({ length: 6 }, (_, i) => i + 3).map(
                            (feet) => (
                              <option key={feet} value={feet.toString()}>
                                {feet} ft
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <Select
                          value={formData.heightFeet}
                          onValueChange={(value) =>
                            handleInputChange("heightFeet", value)
                          }
                        >
                          <SelectTrigger
                            className={`h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg w-full ${
                              errors.height ? "border-red-500" : ""
                            }`}
                          >
                            <SelectValue placeholder={i18n._(`Feet`)} />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl max-h-[200px] sm:max-h-[var(--radix-select-content-available-height)]">
                            {Array.from({ length: 6 }, (_, i) => i + 3).map(
                              (feet) => (
                                <SelectItem
                                  key={feet}
                                  value={feet.toString()}
                                  className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                                >
                                  {feet} ft
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="w-full sm:flex-1">
                      {isIOSDevice ? (
                        <select
                          value={formData.heightInches}
                          onChange={(e) =>
                            handleInputChange("heightInches", e.target.value)
                          }
                          className={`h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10 ${
                            errors.height ? "border-red-500" : "border-gray-200"
                          }`}
                        >
                          <option value="">{i18n._(`Inches`)}</option>
                          {Array.from({ length: 12 }, (_, i) => i).map(
                            (inches) => (
                              <option key={inches} value={inches.toString()}>
                                {inches} in
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <Select
                          value={formData.heightInches}
                          onValueChange={(value) =>
                            handleInputChange("heightInches", value)
                          }
                        >
                          <SelectTrigger
                            className={`h-12 text-base border-2 border-gray-200 focus:border-[#166B93] transition-colors rounded-lg w-full ${
                              errors.height ? "border-red-500" : ""
                            }`}
                          >
                            <SelectValue placeholder={i18n._(`Inches`)} />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl max-h-[200px] sm:max-h-[var(--radix-select-content-available-height)]">
                            {Array.from({ length: 12 }, (_, i) => i).map(
                              (inches) => (
                                <SelectItem
                                  key={inches}
                                  value={inches.toString()}
                                  className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                                >
                                  {inches} in
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  {/* XXXXX */}
                  {heightInCm && (
                    <div className="mt-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        <span className="text-gray-500">≈</span> {heightInCm} cm
                      </p>
                    </div>
                  )}
                  {errors.height && (
                    <p className="text-red-500 text-sm mt-1">{errors.height}</p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="weight"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                  >

                    <Trans>Weight *</Trans>

                  </Label>
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      value={formData.weight}
                      onChange={(e) => handleInputChange("weight", e.target.value)}
                      className={`h-12 text-base flex-1 min-w-0 ${
                        errors.weight ? "border-red-500" : ""
                      }`}
                      placeholder={i18n._(`e.g., 150`)}
                    />
                    <span className="text-base font-medium text-gray-700 whitespace-nowrap">lbs</span>
                  </div>
                  {weightInKg && (
                    <div className="mt-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        <span className="text-gray-500">≈</span> {weightInKg} kg
                      </p>
                    </div>
                  )}
                  {errors.weight && (
                    <p className="text-red-500 text-sm mt-1">{errors.weight}</p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="dateOfBirth"
                    className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                  >
                    <Trans>Date of Birth *</Trans>
                  </Label>
                  {isIOSDevice ? (
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) =>
                        handleInputChange("dateOfBirth", e.target.value)
                      }
                      className={cn(
                        "h-12 text-base",
                        errors.dateOfBirth && "border-red-500",
                      )}
                    />
                  ) : (
                    <Popover
                      open={isCalendarOpen}
                      onOpenChange={setIsCalendarOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full h-12 text-base justify-start text-left font-normal",
                            !formData.dateOfBirth && "text-muted-foreground",
                            errors.dateOfBirth && "border-red-500",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.dateOfBirth ? (
                            format(new Date(formData.dateOfBirth), "PPP")
                          ) : (
                            <span>{i18n._(`Pick a date`)}</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 overflow-visible bg-white dark:bg-white text-slate-900 dark:text-slate-900 focus:outline-none shadow-lg border-2 border-slate-100 dark:border-slate-100"
                        align="start"
                      >
                        <div className="flex items-center gap-2 p-3 pb-0 [&>*]:!mt-0 [&>*]:!mb-0">
                          <div className="relative">
                            <button
                              type="button"
                              className="flex w-[120px] h-9 items-center justify-between rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#166B93]"
                              onClick={() =>
                                setIsMonthDropdownOpen((open) => !open)
                              }
                            >
                              <span>{months[calendarMonth.getMonth()]}</span>
                              <span className="ml-2 text-[10px] text-slate-500">
                                ▼
                              </span>
                            </button>
                            {isMonthDropdownOpen && (
                              <div className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-slate-200 bg-white text-sm text-slate-900 dark:text-slate-900 shadow-lg">
                                {months.map((month) => (
                                  <button
                                    key={month}
                                    type="button"
                                    className={cn(
                                      "flex w-full items-center px-3 py-1.5 text-left text-slate-900 dark:text-slate-900 hover:bg-[#166B93]/10",
                                      month === months[calendarMonth.getMonth()] &&
                                        "bg-[#166B93]/5 font-medium",
                                    )}
                                    onClick={() => {
                                      handleMonthChange(month);
                                      setIsMonthDropdownOpen(false);
                                    }}
                                  >
                                    {month}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              className="flex w-[100px] h-9 items-center justify-between rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#166B93]"
                              onClick={() =>
                                setIsYearDropdownOpen((open) => !open)
                              }
                            >
                              <span>{calendarMonth.getFullYear()}</span>
                              <span className="ml-2 text-[10px] text-slate-500">
                                ▼
                              </span>
                            </button>
                            {isYearDropdownOpen && (
                              <div className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-slate-200 bg-white text-sm text-slate-900 dark:text-slate-900 shadow-lg">
                                {years.map((year) => (
                                  <button
                                    key={year}
                                    type="button"
                                    className={cn(
                                      "flex w-full items-center px-3 py-1.5 text-left text-slate-900 dark:text-slate-900 hover:bg-[#166B93]/10",
                                      year === calendarMonth.getFullYear() &&
                                        "bg-[#166B93]/5 font-medium",
                                    )}
                                    onClick={() => {
                                      handleYearChange(year.toString());
                                      setIsYearDropdownOpen(false);
                                    }}
                                  >
                                    {year}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <Calendar
                          className="bg-white dark:bg-white pointer-events-auto text-slate-900 dark:text-slate-900 [--cell-size:36px] p-2"
                          mode="single"
                          month={calendarMonth}
                          onMonthChange={setCalendarMonth}
                          selected={
                            formData.dateOfBirth
                              ? new Date(formData.dateOfBirth)
                              : undefined
                          }
                          onSelect={(date) => {
                            if (date) {
                              const offset = date.getTimezoneOffset();
                              const adjustedDate = new Date(
                                date.getTime() - offset * 60 * 1000,
                              );
                              const dateString = adjustedDate
                                .toISOString()
                                .split("T")[0];
                              handleInputChange("dateOfBirth", dateString);
                              setIsCalendarOpen(false);
                            }
                          }}
                          disabled={(date) =>
                            date > new Date() || date < new Date("1900-01-01")
                          }
                          fixedWeeks
                          classNames={{
                            caption: "hidden",
                            caption_label: "hidden",
                            table: "w-auto border-collapse",
                            head_cell:
                              "text-slate-500 dark:text-slate-500 w-9 h-9 font-normal text-[0.8rem]",
                            cell: "text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                            day: cn(
                              "h-9 w-9 !min-h-0 !min-w-0 p-0 font-normal aria-selected:opacity-100 rounded-md",
                              "hover:bg-slate-100 hover:text-slate-900",
                              "aria-selected:!bg-[#166B93] aria-selected:text-white",
                              "aria-selected:hover:!bg-[#165B83] aria-selected:hover:text-white",
                              "focus:!bg-[#166B93] focus:text-white",
                              "aria-selected:focus:!bg-[#166B93] aria-selected:focus:text-white",
                            ),
                            today:
                              "bg-transparent text-slate-900 font-semibold aria-selected:!bg-[#166B93] aria-selected:text-white",
                            outside:
                              "text-slate-300 opacity-50 aria-selected:!bg-transparent aria-selected:text-slate-500 aria-selected:opacity-30",
                            disabled:
                              "text-slate-300 opacity-50 bg-transparent cursor-not-allowed",
                            range_middle:
                              "aria-selected:bg-slate-100 aria-selected:text-slate-900",
                            hidden: "invisible",
                            nav_button: "hidden",
                            nav: "hidden",
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>


              </div>

              {/* Current Medical Conditions */}
              <div>
                <Label className="text-sm sm:text-base font-medium text-gray-700 mb-3 block">

                  <Trans>Current Medical Conditions (Select all that apply)</Trans>

                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {translatedConditionOptions.map((condition) => (
                    <div
                      key={condition.value}
                      className="flex items-center space-x-3"
                    >
                      <Checkbox
                        id={`condition-${condition.value}`}
                        checked={
                          Array.isArray(formData.currentMedicalConditions) &&
                          formData.currentMedicalConditions.includes(
                            condition.value
                          )
                        }
                        onCheckedChange={(checked) =>
                          handleConditionChange(condition.value, !!checked)
                        }
                        className="w-5 h-5 border-2 border-gray-300 data-[state=checked]:bg-[#166B93] data-[state=checked]:border-[#166B93]"
                      />
                      <Label
                        htmlFor={`condition-${condition.value}`}
                        className="text-sm sm:text-base text-gray-700 cursor-pointer"
                      >

                        {condition.translatedLabel}

                      </Label>
                    </div>
                  ))}
                </div>

                {/* Show "Other" input field when "Other" is selected */}
                {Array.isArray(formData.currentMedicalConditions) &&
                  formData.currentMedicalConditions.includes("other") && (
                    <div className="mt-4">
                      <Label
                        htmlFor="otherCondition"
                        className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                      >

                        <Trans>Please specify other condition</Trans>

                      </Label>
                      <Input
                        id="otherCondition"
                        type="text"
                        value={formData.otherCondition}
                        onChange={(e) =>
                          handleInputChange("otherCondition", e.target.value)
                        }
                        className="h-12 text-base"

                        placeholder={i18n._(`Please specify your other medical condition`)}  

                      />
                    </div>
                  )}
              </div>

              {/* Past Surgeries */}
              <div>
                <Label
                  htmlFor="pastSurgeries"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Past Surgeries</Trans>

                </Label>
                <Textarea
                  id="pastSurgeries"
                  value={formData.pastSurgeries}
                  onChange={(e) =>
                    handleInputChange("pastSurgeries", e.target.value)
                  }
                  className="min-h-[80px] text-base"
                  placeholder={i18n._(`List any previous surgeries with approximate dates`)}   
                />
              </div>

              {/* Allergies / Medications */}
              <div>
                <Label
                  htmlFor="allergiesMedications"
                  className="text-sm sm:text-base font-medium text-gray-700 mb-2 block"
                >

                  <Trans>Allergies / Medications</Trans>

                </Label>
                <Textarea
                  id="allergiesMedications"
                  value={formData.allergiesMedications}
                  onChange={(e) =>
                    handleInputChange("allergiesMedications", e.target.value)
                  }
                  className="min-h-[80px] text-base"
                  placeholder={i18n._(`List any allergies or medications`)}
                />
              </div>
            </div>
          </div>

          {/* 3. Lifestyle */}
          <div className="bg-white md:rounded-xl pb-6 md:pb-6 md:p-6 border-b border-gray-100 md:border md:border-gray-200 last:border-0 pt-4 md:pt-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-6">
              <div className="w-8 h-8 bg-[#CEF12F] rounded-full flex items-center justify-center text-gray-900 font-bold">
                3
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 font-nunito">

                  <Trans>Lifestyle Information</Trans>
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  <Trans>Understanding your lifestyle helps with treatment planning</Trans>

                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {/* Smoking */}
              <div>
                <Label
                  htmlFor="smokingHabits"
                  className="text-sm font-medium text-gray-700 mb-2 block whitespace-nowrap"
                >

                  <Trans>Smoking Habits *</Trans>

                </Label>
                {isIOSDevice ? (
                  <select
                    id="smokingHabits"
                    value={formData.smokingHabits}
                    onChange={(e) =>
                      handleInputChange("smokingHabits", e.target.value)
                    }
                    className={`h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10 ${
                      errors.smokingHabits
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                  >
                    <option value="">{i18n._(`Select smoking habits`)}</option>
                    {SMOKING_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {i18n._(option.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Select
                    value={formData.smokingHabits}
                    onValueChange={(value) =>
                      handleInputChange("smokingHabits", value)
                    }
                  >
                    <SelectTrigger
                      className={`h-12 text-base border-2 focus:border-[#166B93] transition-colors rounded-lg ${
                        errors.smokingHabits
                          ? "border-red-500"
                          : "border-gray-200"
                      }`}
                    >
                      <SelectValue
                        placeholder={i18n._(`Select smoking habits`)}
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                      {SMOKING_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                        >
                          {i18n._(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.smokingHabits && (
                  <p className="text-red-500 text-sm mt-1">{errors.smokingHabits}</p>
                )}
              </div>

              {/* Alcohol Use */}
              <div>
                <Label
                  htmlFor="alcoholUse"
                  className="text-sm font-medium text-gray-700 mb-2 block whitespace-nowrap"
                >

                  <Trans>Alcohol Use *</Trans>

                </Label>
                {isIOSDevice ? (
                  <select
                    id="alcoholUse"
                    value={formData.alcoholUse}
                    onChange={(e) =>
                      handleInputChange("alcoholUse", e.target.value)
                    }
                    className={`h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10 ${
                      errors.alcoholUse
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                  >
                    <option value="">{i18n._(`Select alcohol use`)}</option>
                    {ALCOHOL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {i18n._(option.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Select
                    value={formData.alcoholUse}
                    onValueChange={(value) =>
                      handleInputChange("alcoholUse", value)
                    }
                  >
                    <SelectTrigger
                      className={`h-12 text-base border-2 focus:border-[#166B93] transition-colors rounded-lg ${
                        errors.alcoholUse
                          ? "border-red-500"
                          : "border-gray-200"
                      }`}
                    >
                      <SelectValue
                        placeholder={i18n._(`Select alcohol use`)}
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                      {ALCOHOL_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                        >
                          {i18n._(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.alcoholUse && (
                  <p className="text-red-500 text-sm mt-1">{errors.alcoholUse}</p>
                )}
              </div>

              {/* Exercise */}
              <div>
                <Label
                  htmlFor="exerciseFrequency"
                  className="text-sm font-medium text-gray-700 mb-2 block whitespace-nowrap"
                >

                  <Trans>Exercise Frequency *</Trans>

                </Label>
                {isIOSDevice ? (
                  <select
                    id="exerciseFrequency"
                    value={formData.exerciseFrequency}
                    onChange={(e) =>
                      handleInputChange("exerciseFrequency", e.target.value)
                    }
                    className={`h-12 w-full rounded-lg border-2 bg-white px-3 text-base focus:outline-none focus:ring-4 focus:ring-[#166B93]/10 ${
                      errors.exerciseFrequency
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                  >
                    <option value="">
                      {i18n._(`Select exercise frequency`)}
                    </option>
                    {EXERCISE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {i18n._(option.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Select
                    value={formData.exerciseFrequency}
                    onValueChange={(value) =>
                      handleInputChange("exerciseFrequency", value)
                    }
                  >
                    <SelectTrigger
                      className={`h-12 text-base border-2 focus:border-[#166B93] transition-colors rounded-lg ${
                        errors.exerciseFrequency
                          ? "border-red-500"
                          : "border-gray-200"
                      }`}
                    >
                      <SelectValue
                        placeholder={i18n._(`Select exercise frequency`)}
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 shadow-lg rounded-xl">
                      {EXERCISE_OPTIONS.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="hover:bg-[#166B93]/10 focus:bg-[#166B93]/10 rounded-lg"
                        >
                          {i18n._(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.exerciseFrequency && (
                  <p className="text-red-500 text-sm mt-1">{errors.exerciseFrequency}</p>
                )}
              </div>
            </div>
          </div>


          {/* 4. Dental Information Section */}
          {/* Dental Information Section */}


          {isDentistry && (
            <div className="bg-white md:rounded-xl pb-6 md:pb-6 md:p-6 border-b border-gray-100 md:border md:border-gray-200 last:border-0 pt-4 md:pt-6">


              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-[#CEF12F] rounded-full flex items-center justify-center text-gray-900 font-bold">
                  4
                </div>
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-lg">🦷</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                <Trans>Dental Information</Trans>  
                </h3>
              </div>



              <div className="space-y-6">

                <div>
                  <Label className="text-base font-medium text-gray-700 mb-2 block">
                    <Trans>What type of dental procedure are you seeking? *</Trans>
                  </Label>
                  <p className="text-sm text-gray-600 mb-3">
                    <Trans>Select all that apply.</Trans>
                  </p>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border-2 p-4 ${
                      errors.dentalProcedureType
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                  >
                    {(
                      [
                        ["implants", msg`Dental Implants`],
                        ["veneers", msg`Veneers`],
                        ["crowns", msg`Crowns`],
                        ["whitening", msg`Teeth Whitening`],
                        ["orthodontics", msg`Orthodontics/Braces`],
                        ["restoration", msg`General Restoration`],
                        ["cosmetic", msg`Cosmetic Dentistry`],
                        ["other", msg`Other`],
                      ] as const
                    ).map(([value, label]) => (
                      <div
                        key={value}
                        className="flex items-center space-x-2 min-h-touch"
                      >
                        <Checkbox
                          id={`dental-proc-${value}`}
                          checked={coerceDentalProcedureTypes(
                            formData.dentalProcedureType,
                          ).includes(value)}
                          onCheckedChange={(c) =>
                            handleDentalProcedureToggle(value, c as boolean)
                          }
                        />
                        <Label
                          htmlFor={`dental-proc-${value}`}
                          className="font-normal cursor-pointer text-base"
                        >
                          {i18n._(label)}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {errors.dentalProcedureType && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.dentalProcedureType}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="discomfort"
                    className="text-base font-medium text-gray-700 mb-2 block">

                    <Trans>
                    Do you experience any kind of dental discomfort?
                    </Trans>
                    
                  </Label>
                  <Textarea
                    id="discomfort"
                    value={formData.dentalDiscomfort}
                    onChange={(e) =>
                      handleInputChange("dentalDiscomfort", e.target.value)
                    }
                    placeholder={i18n._(`Describe any pain, sensitivity, or discomfort or write 'None'`)}  
                    className="min-h-[100px] text-base rounded-xl border-2 focus:border-[#166B93] transition-colors border-gray-200"
                  />
                </div>
              </div>

            </div>
          )}


          {/* Form Actions */}
          <IntakeNavigation
            onBack={onBack}
            onNext={handleContinue}
            isNextLoading={isSubmitting}
            // loadingLabel={<Trans>Saving Information...</Trans>} // If I want to support this later
             nextLabel={<Trans>Continue</Trans>}
          />


        </div>
      </div>
    </div>
  );
}
